import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  MoreHorizontal,
  Play,
  Pause,
  CheckCircle,
  Eye,
  Pencil,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";
import { Fragment } from "react";
import type { Experiment } from "@/types/experiment";
import { ExperimentStatus } from "@/types/enums";

export type ExperimentAction =
  | "detail"
  | "edit"
  | "delete"
  | "rollout"
  | "start"
  | "pause"
  | "resume"
  | "complete";

type TransitionAction = "start" | "pause" | "resume" | "complete";

const STATUS_TRANSITIONS: Record<ExperimentStatus, TransitionAction[]> = {
  [ExperimentStatus.DRAFT]: ["start"],
  [ExperimentStatus.RUNNING]: ["pause", "complete"],
  [ExperimentStatus.PAUSED]: ["resume", "complete"],
  [ExperimentStatus.COMPLETED]: [],
};

const ACTION_CONFIG: Record<
  TransitionAction,
  { label: string; icon: typeof Play; targetStatus: ExperimentStatus }
> = {
  start: { label: "시작", icon: Play, targetStatus: ExperimentStatus.RUNNING },
  pause: {
    label: "일시정지",
    icon: Pause,
    targetStatus: ExperimentStatus.PAUSED,
  },
  resume: {
    label: "재개",
    icon: Play,
    targetStatus: ExperimentStatus.RUNNING,
  },
  complete: {
    label: "완료",
    icon: CheckCircle,
    targetStatus: ExperimentStatus.COMPLETED,
  },
};

interface ExperimentActionsProps {
  experiment: Experiment;
  onAction: (experiment: Experiment, action: ExperimentAction) => void;
}

export function ExperimentActions({
  experiment,
  onAction,
}: ExperimentActionsProps) {
  const transitions = STATUS_TRANSITIONS[experiment.status] ?? [];
  const isRunning = experiment.status === ExperimentStatus.RUNNING;
  const canUpdateRollout =
    experiment.status === ExperimentStatus.RUNNING ||
    experiment.status === ExperimentStatus.PAUSED;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          aria-label="작업 메뉴 열기"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => onAction(experiment, "detail")}
          className="cursor-pointer"
        >
          <Eye className="mr-1 h-4 w-4" />
          상세 보기
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => onAction(experiment, "edit")}
          className="cursor-pointer"
        >
          <Pencil className="mr-1 h-4 w-4" />
          수정
        </DropdownMenuItem>

        {canUpdateRollout && (
          <DropdownMenuItem
            onClick={() => onAction(experiment, "rollout")}
            className="cursor-pointer"
          >
            <SlidersHorizontal className="mr-1 h-4 w-4" />
            rollout 조정
          </DropdownMenuItem>
        )}

        {transitions.length > 0 && <DropdownMenuSeparator />}

        {transitions.map((action, index) => {
          const config = ACTION_CONFIG[action];
          const isDestructive = action === "complete";

          return (
            <Fragment key={action}>
              {isDestructive && index > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={() => onAction(experiment, action)}
                className={
                  isDestructive
                    ? "text-amber-600 focus:text-amber-600 cursor-pointer"
                    : "cursor-pointer"
                }
              >
                <config.icon className="mr-1 h-4 w-4" />
                {config.label}
              </DropdownMenuItem>
            </Fragment>
          );
        })}

        {!isRunning && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onAction(experiment, "delete")}
              className="text-destructive focus:text-destructive cursor-pointer"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              삭제
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function getTargetStatus(action: TransitionAction): ExperimentStatus {
  return ACTION_CONFIG[action].targetStatus;
}
